import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { Table } from "../../components/Table";
import Stack from "@mui/material/Stack";
import { useStyles } from "./styles";
import { useMutation, useQuery } from "react-query";
import {
  listCampaignsAwarded,
  updateCampaignsAwarded,
} from "../../services/campaignAwarded";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import {
  Autorenew,
  Edit,
  EmojiEvents,
  OndemandVideo,
  Visibility,
} from "@mui/icons-material";
import { useCallback, useState } from "react";
import { queryClient } from "../../services/queryClient";
import { NewAwarded } from "./New";
import Switch from "@mui/material/Switch";
import { format } from "date-fns";
import { CampaignAwarded as TCampaignAwarded } from "../../types/campaignAwarded";
import { EditAwarded } from "./Edit";
import { useToast } from "../../hooks/useToast";
import { DetailAwarded } from "./Detail";
import { useNavigate, useSearchParams } from "react-router-dom";
import { VideoAwarded } from "./Video";

export function CampaignAwarded() {
  const styles = useStyles();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [awarded, setAwarded] = useState<TCampaignAwarded | null>();
  const [dialogNewAwarded, setDialogNewAwarded] = useState<boolean>(
    Boolean(searchParams.get("create"))
  );
  const [dialogEditAwarded, setDialogEditAwarded] = useState<boolean>(false);
  const [dialogDetailAwarded, setDialogDetailAwarded] =
    useState<boolean>(false);
  const [dialogVideoAwarded, setDialogVideoAwarded] = useState<boolean>(false);

  const campaignsAwarded = useQuery("campaignsAwarded", listCampaignsAwarded);

  const invalidateAwarded = useCallback(() => {
    queryClient.invalidateQueries("campaignsAwarded");
  }, []);

  const editCampaignAwarded = useMutation(
    async (data: any) => {
      const response = await updateCampaignsAwarded(data);
      return response.message;
    },
    {
      onSuccess: () => {
        toast.show("Ação atualizada com sucesso!", { severity: "success" });
        invalidateAwarded();
      },
      onError: () => {
        toast.show("Erro ao editar a ação premiada", { severity: "error" });
      },
    }
  );

  const handleEditAwarded = useCallback(
    async (checked: boolean, item: TCampaignAwarded) => {
      const enableAwardedData = { ...item, active: checked ? 1 : 0 };
      await editCampaignAwarded.mutateAsync(enableAwardedData);
    },
    [editCampaignAwarded]
  );

  const handleDialogNewAwarded = useCallback(() => {
    setDialogNewAwarded((prev) => !prev);
  }, []);

  const handleDialogVideoAwarded = useCallback((data) => {
    setAwarded(data);
    setDialogVideoAwarded((prev) => !prev);
  }, []);

  const handleDialogEditAwarded = useCallback((data) => {
    setAwarded(data);
    setDialogEditAwarded((prev) => !prev);
  }, []);

  const handleDialogDetailAwarded = useCallback((data) => {
    setAwarded(data);
    setDialogDetailAwarded((prev) => !prev);
  }, []);

  const handleGoPrizeDrawPage = useCallback(
    (item: TCampaignAwarded) => {
      navigate(`${item.id}`, { state: item });
    },
    [navigate]
  );

  const RenderActions = (item: TCampaignAwarded) => {
    return (
      <Stack spacing={1} direction="row">
        <Tooltip title="Ver detalhes" placement="top">
          <IconButton
            onClick={() => {
              handleDialogDetailAwarded(item);
            }}
          >
            <Visibility />
          </IconButton>
        </Tooltip>
        <Tooltip
          hidden={Boolean(item.processed)}
          title="Editar"
          placement="top"
        >
          <IconButton
            disabled={Boolean(item.processed)}
            onClick={() => {
              handleDialogEditAwarded(item);
            }}
          >
            <Edit />
          </IconButton>
        </Tooltip>
        {item.processed ? (
          <Tooltip
            title={
              !Boolean(item.video) ? "Processando vídeo" : "Vídeo do sorteio"
            }
            placement="top"
          >
            <IconButton
              onClick={() => {
                handleDialogVideoAwarded(item);
              }}
              disabled={!Boolean(item.video)}
            >
              <OndemandVideo />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Realizar sorteio" placement="top">
            <IconButton
              onClick={() => {
                handleGoPrizeDrawPage(item);
              }}
              disabled={!Boolean(item.active)}
            >
              <EmojiEvents />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    );
  };

  return (
    <>
      <div className={styles.root}>
        <Typography variant="h4">Ações premiadas</Typography>
      </div>
      <div className={styles.rootActions}>
        <Stack direction="row" spacing={1}>
          <Button data-testid="awarded-btn-prize-draw" variant="contained">
            Sorteio
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={handleDialogNewAwarded}
            data-testid="awarded-btn-prize-draw"
            variant="contained"
          >
            Nova ação premiada
          </Button>
          <Tooltip title="Atualizar lista" placement="top">
            <IconButton onClick={invalidateAwarded}>
              <Autorenew />
            </IconButton>
          </Tooltip>
        </Stack>
      </div>
      <Paper variant="outlined" elevation={0} className={styles.paper}>
        <Table
          pageSize={7}
          toolbar={false}
          isLoading={
            campaignsAwarded.isFetching || editCampaignAwarded.isLoading
          }
          data={campaignsAwarded.data as []}
          style={{ backgroundColor: "transparent" }}
          headerStyle={{ backgroundColor: "transparent" }}
          columns={[
            { title: "Nome", field: "name" },
            {
              title: "Público",
              field: "form_id",
              render: (row) => (row.form_id ? "Formulários" : "Audiências"),
            },
            {
              title: "Ativo",
              align: "center",
              field: "ativo",
              render: (row) => (
                <Switch
                  disabled={Boolean(row.processed)}
                  onChange={(_, checked) => handleEditAwarded(checked, row)}
                  checked={Boolean(row.active)}
                />
              ),
            },
            {
              title: "Criado em",
              field: "data_insert",
              render: (row) =>
                format(
                  new Date(row?.date_insert?.replace("Z", "")),
                  "dd/MM/yyyy HH:mm"
                ),
            },
            {
              title: "Ações",
              render: RenderActions,
            },
          ]}
          options={{
            actionsColumnIndex: -1,
            pageSize: 7,
            selectionProps: () => ({
              color: "primary",
            }),
          }}
        />
      </Paper>
      <NewAwarded open={dialogNewAwarded} onClose={handleDialogNewAwarded} />
      <EditAwarded
        data={awarded}
        open={dialogEditAwarded}
        onClose={() => {
          handleDialogEditAwarded(null);
        }}
      />
      <DetailAwarded
        data={awarded}
        open={dialogDetailAwarded}
        onClose={() => {
          handleDialogDetailAwarded(null);
        }}
      />
      <VideoAwarded
        data={awarded}
        open={dialogVideoAwarded}
        onClose={() => {
          handleDialogVideoAwarded(null);
        }}
      />
    </>
  );
}
